import React from "react";

interface Props {
  totalWeeks: number;
  value: number;
  onChange: (week: number) => void;
  loading?: boolean;
  weeksWithEntries?: Set<number>;
  /**
   * Weeks holding a review that is waiting on WHOEVER IS LOOKING — the
   * candidate while they have not answered, the supervisor once they have.
   * The server resolves the side; this only paints it.
   *
   * Shaded red, and it OUTRANKS the selected-week highlight: clicking into a
   * flagged week must not make the flag disappear under your own selection —
   * that is exactly the moment it still needs work.
   */
  weeksWithOpenReview?: Set<number>;
  /** What the red means for this viewer. */
  reviewNote?: string;
}

const WeekCalendar: React.FC<Props> = ({
  totalWeeks,
  value,
  onChange,
  loading,
  weeksWithEntries,
  weeksWithOpenReview,
  reviewNote,
}) => {
  const weeks = React.useMemo(() => {
    const n = Math.max(0, Number(totalWeeks || 0));
    return Array.from({ length: n }, (_, i) => i + 1);
  }, [totalWeeks]);

  if (!weeks.length) {
    return (
      <div className="bg-white p-4 rounded-xl border">
        <p className="text-xs text-gray-500">No weeks available.</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-4 rounded-xl border">
      <div className="flex items-center justify-between">
        <h4 className="font-bold text-sm">Weeks</h4>
        {loading ? (
          <span className="text-[11px] text-gray-500">Loading…</span>
        ) : (
          <span className="text-[11px] text-gray-500">
            {value} / {weeks.length}
          </span>
        )}
      </div>

      <div className="mt-3 grid grid-cols-7 gap-2">
        {weeks.map((w) => {
          const active = w === value;
          const hasEntries = weeksWithEntries?.has(w);
          const openReview = weeksWithOpenReview?.has(w);

          return (
            <button
              key={w}
              type="button"
              onClick={() => onChange(w)}
              className={[
                "h-9 rounded-lg border text-xs font-medium transition",
                openReview
                  ? active
                    ? "bg-red-600 text-white border-red-700 ring-2 ring-red-300"
                    : "bg-red-100 text-red-800 border-red-300 hover:bg-red-200"
                  : active
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : hasEntries
                  ? "bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50",
              ].join(" ")}
              title={
                openReview
                  ? `Week ${w} — ${reviewNote || "a review is waiting on you"}`
                  : hasEntries
                  ? `Week ${w} (has entries)`
                  : `Week ${w}`
              }
            >
              {w}
            </button>
          );
        })}
      </div>

      {weeksWithOpenReview && weeksWithOpenReview.size > 0 ? (
        <p className="mt-3 flex items-center gap-1.5 text-[11px] text-red-700">
          <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-sm border border-red-300 bg-red-100" />
          {weeksWithOpenReview.size} week
          {weeksWithOpenReview.size === 1 ? "" : "s"} waiting on you —{" "}
          {reviewNote || "a review needs your attention"}.
        </p>
      ) : null}

      <p className="mt-3 text-[11px] text-gray-500">
        Tap a week number to view/edit entries.
      </p>
    </div>
  );
};

export default WeekCalendar;
